import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { useAuth } from '../../contexts/AuthContext';

const SubscriptionSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { needsOnboarding, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(5);

  const sessionId = searchParams.get('session_id');

  // Refresh user data to get updated subscription
  useEffect(() => {
    refreshUser();
  }, []);

  useEffect(() => {
    // Determine where to redirect
    const redirectTo = needsOnboarding ? '/onboarding' : '/dashboard';

    // Auto redirect after countdown
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(redirectTo);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, needsOnboarding]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-primary-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <Card className="max-w-md w-full shadow-xl">
        <CardContent className="pt-8 pb-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>

            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {needsOnboarding
                ? (t('subscription.welcomeaboard') || 'Welcome Aboard!')
                : (t('subscription.paymentSuccess') || 'Payment Successful!')}
            </h1>

            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {needsOnboarding
                ? (t('subscription.trialStarted') || 'Your free trial has started! Let\'s set up your account.')
                : (t('subscription.subscriptionActivated') || 'Your subscription is now active. Thank you for your purchase!')}
            </p>

            {needsOnboarding && (
              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-primary-700 dark:text-primary-300">
                  <Sparkles className="w-5 h-5" />
                  <span className="font-medium">{t('subscription.trialInfo') || '7 days free trial'}</span>
                </div>
              </div>
            )}

            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('subscription.redirectingIn') || 'Redirecting in'} {countdown} {t('subscription.seconds') || 'seconds'}...
              </p>
            </div>

            <div className="space-y-3">
              <Button
                className="w-full"
                onClick={() => navigate(needsOnboarding ? '/onboarding' : '/dashboard')}
              >
                {needsOnboarding
                  ? (t('subscription.getStarted') || 'Get Started')
                  : (t('nav.dashboard'))}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              {!needsOnboarding && (
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => navigate('/subscription')}
                >
                  {t('subscription.viewSubscription') || 'View Subscription'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccessPage;
